// src/store/applicationTypes/applicationTypesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';

// Async thunks
export const fetchApplicationTypes = createAsyncThunk(
  'applicationTypes/fetchApplicationTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getApplicationTypes();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const createApplicationType = createAsyncThunk(
  'applicationTypes/createApplicationType',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiService.createApplicationType(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateApplicationType = createAsyncThunk(
  'applicationTypes/updateApplicationType',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateApplicationType(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteApplicationType = createAsyncThunk(
  'applicationTypes/deleteApplicationType',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deleteApplicationType(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// Initial state
const initialState = {
  applicationTypes: [],
  isLoading: false,
  error: null,
  successMessage: null
};

// Create the slice
const applicationTypesSlice = createSlice({
  name: 'applicationTypes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch application types
      .addCase(fetchApplicationTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchApplicationTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.applicationTypes = action.payload;
      })
      .addCase(fetchApplicationTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Create application type
      .addCase(createApplicationType.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createApplicationType.fulfilled, (state, action) => {
        state.isLoading = false;
        state.applicationTypes.push(action.payload);
        state.successMessage = 'Application type created successfully';
      })
      .addCase(createApplicationType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Update application type
      .addCase(updateApplicationType.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateApplicationType.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.applicationTypes.findIndex(type => type.id === action.payload.id);
        if (index !== -1) {
          state.applicationTypes[index] = action.payload;
        }
        state.successMessage = 'Application type updated successfully';
      })
      .addCase(updateApplicationType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Delete application type
      .addCase(deleteApplicationType.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteApplicationType.fulfilled, (state, action) => {
        state.isLoading = false;
        state.applicationTypes = state.applicationTypes.filter(type => type.id !== action.payload);
        state.successMessage = 'Application type deleted successfully';
      })
      .addCase(deleteApplicationType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
  }
});

export const { clearError, clearSuccessMessage } = applicationTypesSlice.actions;
export default applicationTypesSlice.reducer;